import Typography from '@mui/material/Typography'
import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Loader from 'components/Loader/Loader'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MISSION_CALCULATING_STATUSES, PROGRESS_TYPES } from 'utils/constants'
import { dispatchBlockReason } from 'utils/robotDispatch'
import { IMissionInfo } from 'utils/interfaces'
import { VENDOR, type VendorCode, getVendorDisplayName } from 'utils/vendorDisplayName'

import MissionsList from './MissionsList/MissionsList'
import ScenariosList from './ScenariosList/ScenariosList'

import style from './SelectMissionModal.module.css'

interface IDialogProps {
  selectedMissionId?: number
  selectedRobotId?: number
  selectedRobotSerial?: string
  isOpen: boolean
  robotType: string | undefined
  only_ok_missions?: boolean
  showScenariosTab?: boolean
  forceScenariosOnly?: boolean
  allowEmptyScenarios?: boolean
  /** When true, hide scenarios with mixed mission types (use for task assignment) */
  hideInconsistentScenarios?: boolean
  /**
   * Robot is already chosen. `robotType` is that robot: Combo work is included
   * for Omni, except scenarios whose stations that robot cannot use.
   */
  dispatchToRobot?: boolean
  selectedVendor?: VendorCode
  onVendorChange?: (vendor: VendorCode) => void
  lockVendor?: boolean
  onConfirmClick: (mission: IMissionInfo | any) => void
  onClose: () => void
}

const SelectMissionModal = ({
  isOpen,
  robotType,
  only_ok_missions = true,
  showScenariosTab = false,
  forceScenariosOnly = false,
  allowEmptyScenarios = false,
  hideInconsistentScenarios = false,
  dispatchToRobot = false,
  selectedVendor,
  onVendorChange,
  lockVendor = false,
  onConfirmClick,
  onClose,
  selectedMissionId,
  selectedRobotId,
  selectedRobotSerial,
}: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [missions, setMissions] = useState<IMissionInfo[]>([])
  const [scenarios, setScenarios] = useState<any[]>([])
  const [selectedMission, setSelectedMission] = useState<IMissionInfo | any | undefined>(undefined)
  /** GS vendor: executable tasks for selected robot */
  const [gausiumExecTasks, setGausiumExecTasks] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [vendorFilter, setVendorFilter] = useState<VendorCode>(selectedVendor || VENDOR.AUTONOMICS)
  const [page, setPage] = useState(1)
  const [activeTab, setActiveTab] = useState(forceScenariosOnly || selectedVendor === VENDOR.GAUSIUM ? 1 : 0) // 0 for Missions, 1 for Scenarios
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const PAGE_SIZE = 20
  const isGausiumVendor = vendorFilter === VENDOR.GAUSIUM
  const vendorOptions: IOption[] = [
    { value: VENDOR.AUTONOMICS, text: getVendorDisplayName(VENDOR.AUTONOMICS) },
    { value: VENDOR.GAUSIUM, text: getVendorDisplayName(VENDOR.GAUSIUM) },
  ]

  useEffect(() => {
    if (selectedVendor) setVendorFilter(selectedVendor)
  }, [selectedVendor])

  useEffect(() => {
    if (!isGausiumVendor && forceScenariosOnly) {
      setActiveTab(1)
    } else if (isGausiumVendor) {
      setActiveTab(1)
    } else {
      setActiveTab(0)
    }
    setSelectedMission(undefined)
  }, [isGausiumVendor, forceScenariosOnly])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchData = async (pageNum: number, search: string, isAppend = false) => {
    try {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.WORK)
      } else {
        setIsFetchingMore(true)
      }

      let responseCount = 0

      if (isGausiumVendor) {
        const raw = selectedRobotId
          ? await RobotApi.fetchGausiumExecutableTasksRecent(selectedRobotId)
          : await RobotApi.fetchGausiumExecutableTasksPresentAll()
        const q = (search || '').trim().toLowerCase()
        const filtered = q
          ? raw.filter(
              t =>
                String(t.robot_sn || '')
                  .toLowerCase()
                  .includes(q) ||
                String(t.task_name || '')
                  .toLowerCase()
                  .includes(q) ||
                String(t.map_name || '')
                  .toLowerCase()
                  .includes(q) ||
                String(t.robot_sn || '')
                  .toLowerCase()
                  .includes(q)
            )
          : raw
        if (!isAppend) setGausiumExecTasks(filtered)
        else setGausiumExecTasks(prev => [...prev, ...filtered])
        responseCount = filtered.length
        setHasMore(false)
      } else if (activeTab === 0) {
        const { results, count } = await MainApi.fetchMissions(
          pageNum,
          PAGE_SIZE,
          robotType,
          search || undefined,
          undefined,
          dispatchToRobot
        )
        const filteredResults = only_ok_missions
          ? results.filter(mission => mission.status === MISSION_CALCULATING_STATUSES.OK)
          : results

        if (isAppend) {
          setMissions(prev => [...prev, ...filteredResults])
        } else {
          setMissions(filteredResults)
        }
        responseCount = count || 0
      } else {
        const { results, count } = await MainApi.fetchScenarios(
          pageNum,
          PAGE_SIZE,
          search || undefined,
          robotType,
          undefined,
          undefined,
          undefined,
          dispatchToRobot
        )

        if (isAppend) {
          setScenarios(prev => [...prev, ...results])
        } else {
          setScenarios(results)
        }
        responseCount = count || 0
      }

      setHasMore(pageNum * PAGE_SIZE < responseCount)
      setPage(pageNum)

      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      }
    } catch (error) {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.ERROR)
      }
    } finally {
      setIsFetchingMore(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchData(1, debouncedSearchQuery, false)
    } else {
      setSelectedMission(undefined)
      setSearchQuery('')
      setDebouncedSearchQuery('')
      setMissions([])
      setScenarios([])
      setGausiumExecTasks([])
      setPage(1)
      setHasMore(true)
      setActiveTab(forceScenariosOnly || selectedVendor === VENDOR.GAUSIUM ? 1 : 0)
      setVendorFilter(selectedVendor || VENDOR.AUTONOMICS)
      setLoadingProgress(PROGRESS_TYPES.IDLE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearchQuery, activeTab, isGausiumVendor, selectedRobotId, selectedRobotSerial])

  const loadMore = () => {
    if (loadingProgress === PROGRESS_TYPES.SUCCESS && hasMore && !isFetchingMore) {
      fetchData(page + 1, debouncedSearchQuery, true)
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMore()
    }
  }

  const selectedDispatchBlock =
    dispatchToRobot && selectedMission
      ? dispatchBlockReason({
          entityType: selectedMission.robot_type,
          robotType,
          maintenance: activeTab === 1 ? selectedMission.maintenance : undefined,
        })
      : undefined

  const handleConfirm = () => {
    if (isGausiumVendor) {
      if (!selectedMission) return
      onConfirmClick({
        id: selectedMission.id,
        is_scenario: true,
        is_gausium_executable_task: true,
        name: selectedMission.task_name || selectedMission.name,
        gausiumMapName: selectedMission.map_name || '',
        map_name: selectedMission.map_name || '',
        gausiumRobotId: selectedMission.robot_id,
        robot_type: selectedMission.robot_type,
        status: MISSION_CALCULATING_STATUSES.OK,
      })
      return
    }
    if (activeTab === 0) {
      onConfirmClick(selectedMission)
    } else {
      if (!isGausiumVendor && selectedMission?.service_points_incomplete) {
        return
      }
      if (selectedDispatchBlock) return
      if (forceScenariosOnly) {
        onConfirmClick(selectedMission)
        return
      }
      if (!selectedMission || selectedMission.missions_ids?.length === 0) return
      const fakeMission = {
        id: selectedMission.id,
        is_scenario: true,
        name: selectedMission.name,
        missions_ids: selectedMission.missions_ids,
        robot_type: selectedMission.robot_type,
        status: MISSION_CALCULATING_STATUSES.OK,
        resource_profile: selectedMission.resource_profile,
        maintenance: selectedMission.maintenance,
        service_points_incomplete: selectedMission.service_points_incomplete,
      }
      onConfirmClick(fakeMission as any)
    }
  }

  return (
    <ConfirmDialog
      isOpen={isOpen}
      className={style.root}
      onClose={onClose}
      onConfirmClick={handleConfirm}
      title={t('robot.modal_select_mission_title')}
      titleRight={
        <Dropdown
          minWidth={160}
          options={lockVendor ? vendorOptions.filter(o => o.value === vendorFilter) : vendorOptions}
          currentOption={{ value: vendorFilter, text: getVendorDisplayName(vendorFilter) }}
          onClickOption={(option: IOption) => {
            const next = option.value as VendorCode
            setVendorFilter(next)
            onVendorChange?.(next)
            setSelectedMission(undefined)
            setSearchQuery('')
          }}
          disabled={lockVendor}
        />
      }
      confirmText={t('robot.modal_select_mission_confirm')}
      confirmButtonDisabled={
        !selectedMission ||
        Boolean(selectedDispatchBlock) ||
        Boolean(
          (forceScenariosOnly || activeTab === 1) &&
            !isGausiumVendor &&
            selectedMission?.service_points_incomplete
        )
      }
    >
      <div className={style.content}>
        {showScenariosTab && !forceScenariosOnly && !isGausiumVendor && (
          <AdaptiveTabs
            value={activeTab}
            onChange={(e, newValue: number) => {
              setActiveTab(newValue)
              setSelectedMission(undefined)
              setSearchQuery('')
            }}
            indicatorColor="primary"
            sx={{ height: 45, minHeight: 45, marginBottom: '16px' }}
          >
            <StyledTab
              label="Missions"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
            <StyledTab
              label="Scenarios"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
          </AdaptiveTabs>
        )}
        <input
          className={style.searchInput}
          type="text"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ marginBottom: '16px' }}
        />
        {loadingProgress === PROGRESS_TYPES.WORK && <Loader />}

        <TabPanel value={activeTab} index={0} disablePadding={true}>
          {loadingProgress === PROGRESS_TYPES.SUCCESS && missions.length > 0 && (
            <MissionsList
              missions={missions}
              onSelect={mission => setSelectedMission(mission)}
              selectedMission={selectedMission}
              onScrollBottom={loadMore}
            />
          )}
          {loadingProgress === PROGRESS_TYPES.SUCCESS && missions.length === 0 && (
            <Typography variant="body1" align="center">
              {t('robot.modal_missions_empty')}
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1} disablePadding={true}>
          {isGausiumVendor && loadingProgress === PROGRESS_TYPES.SUCCESS && gausiumExecTasks.length > 0 && (
            <div className={style.mapsTableLikeAuto} onScroll={handleScroll}>
              <div className={style.mapsTableHeaderLikeAuto}>
                <span>Robot</span>
                <span>Task name</span>
                <span>Map</span>
              </div>
              {gausiumExecTasks.map(task => (
                <div
                  key={task.id}
                  className={
                    selectedMission?.id === task.id ? style.mapsTableRowSelectedLikeAuto : style.mapsTableRowLikeAuto
                  }
                  onClick={() => setSelectedMission(task)}
                  title={task.vendor_task_uuid ? `UUID: ${task.vendor_task_uuid}` : undefined}
                >
                  <span>{task.robot_sn || '—'}</span>
                  <span className={style.colLeftMultiline}>{task.task_name || '—'}</span>
                  <span className={style.colLeftMultiline}>{task.map_name || '—'}</span>
                </div>
              ))}
            </div>
          )}
          {isGausiumVendor && loadingProgress === PROGRESS_TYPES.SUCCESS && gausiumExecTasks.length === 0 && (
            <Typography variant="body1" align="center">
              No executable tasks found
            </Typography>
          )}
          {!isGausiumVendor && loadingProgress === PROGRESS_TYPES.SUCCESS && scenarios.length > 0 && (
            <ScenariosList
              scenarios={scenarios}
              onSelect={scenario => setSelectedMission(scenario)}
              selectedScenario={selectedMission}
              onScrollBottom={loadMore}
              allowEmptyScenarios={allowEmptyScenarios}
              hideInconsistentScenarios={hideInconsistentScenarios}
              targetRobotType={dispatchToRobot ? robotType : undefined}
            />
          )}
          {!isGausiumVendor && loadingProgress === PROGRESS_TYPES.SUCCESS && scenarios.length === 0 && (
            <Typography variant="body1" align="center">
              No scenarios found
            </Typography>
          )}
        </TabPanel>

        {isFetchingMore && (
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <Loader />
          </div>
        )}
      </div>
    </ConfirmDialog>
  )
}

export default SelectMissionModal
